import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import vehicleHistoryService, { RiderVehicleHistory } from '../../services/vehicleHistoryService';
import vehicleService from '../../services/vehicleService';

interface RiderVehicleHistoryTabProps {
  vehicleHistory: RiderVehicleHistory[];
  loading: boolean;
  onVehicleHistoryUpdated?: () => Promise<void> | void;
}

const RiderVehicleHistoryTab: React.FC<RiderVehicleHistoryTabProps> = ({
  vehicleHistory,
  loading,
  onVehicleHistoryUpdated,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLabel, setPreviewLabel] = useState<string>('');
  const [selectedHistory, setSelectedHistory] = useState<RiderVehicleHistory | null>(null);
  const [batteryNumber, setBatteryNumber] = useState<string | null>(null);
  const [chargerNumber, setChargerNumber] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [batteryImage, setBatteryImage] = useState<File | null>(null);
  const [chargerImage, setChargerImage] = useState<File | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  const activeAssignment = vehicleHistory.find((h) => h.status === 'ACTIVE') || null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const value = String(dateString).trim().replace('T', ' ').replace(/Z$/i, '').split('.')[0];
    return value.split(' ')[0] || value;
  };

  const renderImageCards = (
    title: string,
    images?: {
      front?: string | null;
      back?: string | null;
      left?: string | null;
      right?: string | null;
      odometer?: string | null;
      virtual?: string | null;
      charger?: string | null;
      battery?: string | null;
    },
  ) => {
    if (!images) return null;

    const entries = [
      ['front', images.front],
      ['back', images.back],
      ['left', images.left],
      ['right', images.right],
      ['Rider with Vehicle', images.odometer],
      ['Virtual Document', images.virtual],
      ['Charger', images.charger],
      ['Battery', images.battery],
    ] as const;

    const available = entries.filter(([, url]) => Boolean(url));
    if (!available.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No {title.toLowerCase()} available.
        </Typography>
      );
    }

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Grid container spacing={1}>
          {available.map(([label, url]) => (
            <Grid item xs={12} key={label}>
              <Card variant="outlined" sx={{ cursor: 'pointer', overflow: 'hidden' }}>
                <CardContent sx={{ p: 1 }}>
                  <Tooltip title={`Open ${label} image`} arrow>
                    <Box
                      component="img"
                      src={url!}
                      alt={`${title} ${label}`}
                      loading="lazy"
                      sx={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 1, mb: 1 }}
                      onClick={() => {
                        setPreviewUrl(url!);
                        setPreviewLabel(`${title} - ${label}`);
                      }}
                    />
                  </Tooltip>
                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center' }}>
                    {label.charAt(0).toUpperCase() + label.slice(1)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  useEffect(() => {
    let mounted = true;
    async function lookupVehicle() {
      // Only lookup when the rider has an ACTIVE assignment
      if (!activeAssignment) {
        if (mounted) {
          setBatteryNumber(null);
          setChargerNumber(null);
          setLookupLoading(false);
        }
        return;
      }

      try {
        setLookupLoading(true);
        const reg = activeAssignment.registrationNumber || '';
        if (!reg) {
          if (mounted) {
            setBatteryNumber(null);
            setChargerNumber(null);
            setLookupLoading(false);
          }
          return;
        }

        // Search vehicles by registration number (use search param)
        const resp: any = await vehicleService.getVehicles({ search: reg }, { limit: 1 });
        const found = resp?.data?.[0] || resp?.vehicles?.[0] || null;

        if (mounted && found) {
          setBatteryNumber(found.batteryNumber || null);
          setChargerNumber(found.chargerNumber || null);
        } else if (mounted) {
          setBatteryNumber(null);
          setChargerNumber(null);
        }
      } catch (err: any) {
        console.error('Failed to lookup vehicle for registration', activeAssignment?.registrationNumber, err?.message || err);
        if (mounted) {
          setBatteryNumber(null);
          setChargerNumber(null);
        }
      } finally {
        if (mounted) setLookupLoading(false);
      }
    }

    lookupVehicle();
    return () => {
      mounted = false;
    };
  }, [activeAssignment]);

  const resetUploadState = () => {
    setBatteryImage(null);
    setChargerImage(null);
    setUploadError(null);
    setUploadSuccess(null);
  };

  const handleUpdateBatteryChargerImages = async () => {
    if (!selectedHistory?.riderId) {
      setUploadError('Rider details are missing for this history record.');
      return;
    }

    if (selectedHistory.status !== 'ACTIVE') {
      setUploadError('Battery and charger images can be updated only for the active assignment.');
      return;
    }

    if (!batteryImage && !chargerImage) {
      setUploadError('Please select a battery image, charger image, or both.');
      return;
    }

    try {
      setUploadingImages(true);
      setUploadError(null);
      setUploadSuccess(null);

      const response = await vehicleHistoryService.updateActiveBatteryAndChargerImages(
        selectedHistory.riderId,
        {
          batteryImage,
          chargerImage,
        },
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to update images');
      }

      resetUploadState();
      setUploadSuccess('Battery and charger images updated successfully.');
      await onVehicleHistoryUpdated?.();
    } catch (error: any) {
      setUploadError(error.message || 'Failed to update battery and charger images.');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleCloseHistoryDialog = () => {
    resetUploadState();
    setSelectedHistory(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (vehicleHistory.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        This rider has no vehicle assignment history.
      </Alert>
    );
  }

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">Vehicle Registration Number</Typography>
              <Typography variant="h6" fontWeight="medium">{activeAssignment ? (activeAssignment.registrationNumber || 'N/A') : 'N/A'}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">Battery Number</Typography>
              <Typography variant="body1" fontWeight="medium">
                {lookupLoading ? <CircularProgress size={18} /> : (batteryNumber || 'N/A')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">Charger Number</Typography>
              <Typography variant="body1" fontWeight="medium">
                {lookupLoading ? <CircularProgress size={18} /> : (chargerNumber || 'N/A')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Vehicle Assignment History
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Registration Number</TableCell>
                {/* <TableCell>Make & Model</TableCell> */}
                <TableCell>Client Name</TableCell>
                <TableCell>Assigning Odometer</TableCell>
                <TableCell>Returning Odometer</TableCell>
                <TableCell>Assigned Date</TableCell>
                <TableCell>Returned Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Assigned By</TableCell>
                <TableCell>Daily Rent</TableCell>
                <TableCell>Returned By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicleHistory.map((history) => (
                <TableRow
                  key={history.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  selected={selectedHistory?.id === history.id}
                  onClick={() => setSelectedHistory(history)}
                >
                  <TableCell>{history.registrationNumber || 'N/A'}</TableCell>
                  {/* <TableCell>
                    {history.vehicleMake && history.vehicleModel
                      ? `${history.vehicleMake} ${history.vehicleModel}`
                      : 'N/A'}
                  </TableCell> */}
                  <TableCell>{history.clientName || 'N/A'}</TableCell>
                  <TableCell>{history.AssigningOdometerCount ?? 'N/A'}</TableCell>
                  <TableCell>{history.ReturningOdometerCount ?? 'N/A'}</TableCell>
                  <TableCell>{formatDate(history.assignedAt)}</TableCell>
                  <TableCell>
                    {history.returnedAt ? formatDate(history.returnedAt) : 'Currently Assigned'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={history.status}
                      color={history.status === 'ACTIVE' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{history.notes || '-'}</TableCell>
                  <TableCell>{history.vehicleAgeDays || 'N/A'}</TableCell>
                  <TableCell>{history.assignedBy || '-'}</TableCell>
                  <TableCell>
                    {history.dailyRent !== null && history.dailyRent !== undefined
                      ? `₹${history.dailyRent}`
                      : '-'}
                  </TableCell>
                  <TableCell>{history.returnedBy || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>

      <Dialog open={Boolean(selectedHistory)} onClose={handleCloseHistoryDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedHistory?.registrationNumber
            ? `Images for ${selectedHistory.registrationNumber}`
            : 'Vehicle Images'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Assignment Audit Details
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Assigned By</Typography>
                    <Typography variant="body1">{selectedHistory?.assignedBy || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Returned By</Typography>
                    <Typography variant="body1">{selectedHistory?.returnedBy || '-'}</Typography>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            {selectedHistory?.status === 'ACTIVE' && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Update Battery & Charger Images
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload one or both images for the active assignment.
                  </Typography>

                  {uploadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {uploadError}
                    </Alert>
                  )}
                  {uploadSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {uploadSuccess}
                    </Alert>
                  )}

                  <Grid container spacing={2} alignItems="stretch">
                    {[
                      {
                        label: 'Battery Image',
                        file: batteryImage,
                        setter: setBatteryImage,
                      },
                      {
                        label: 'Charger Image',
                        file: chargerImage,
                        setter: setChargerImage,
                      },
                    ].map(({ label, file, setter }) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Box
                          sx={{
                            height: '100%',
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {label}
                          </Typography>
                          <Button
                            component="label"
                            variant="outlined"
                            disabled={uploadingImages}
                            sx={{ textTransform: 'none' }}
                          >
                            {file ? 'Change image' : 'Choose image'}
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              onChange={(event) => {
                                setter(event.target.files?.[0] || null);
                                setUploadError(null);
                                setUploadSuccess(null);
                              }}
                            />
                          </Button>
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                            {file ? file.name : 'No image selected'}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button onClick={resetUploadState} disabled={uploadingImages || (!batteryImage && !chargerImage)}>
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleUpdateBatteryChargerImages}
                      disabled={uploadingImages || (!batteryImage && !chargerImage)}
                    >
                      {uploadingImages ? 'Updating...' : 'Update Images'}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              {renderImageCards('Assignment Images', selectedHistory?.assignmentImages)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderImageCards('Unassignment Images', selectedHistory?.unassignmentImages)}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(previewUrl)} onClose={() => setPreviewUrl(null)} maxWidth="md" fullWidth>
        <DialogTitle>{previewLabel}</DialogTitle>
        <DialogContent sx={{ p: 0, minHeight: 360, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt={previewLabel}
              sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RiderVehicleHistoryTab;
